import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import httpRequest from 'src/helpers/httpRequest';
import { IChildCategory, IMainCategory, IqueryParams, ISubCategory } from 'src/types/GlobalInterfaces';
import { IPaginated } from 'src/types/paginated';
import qs from "qs";
import ConfirmationModal from 'src/Components/GlobalComponents/ConfirmationModal';
import BreadCrumb from 'src/Components/GlobalComponents/BreadCrumb';
import UpsertChildCategory from 'src/Components/PharmacyComponents/Admin/UpsertChildCategory';
import PharmaCategoryHeader from 'src/Components/PharmacyComponents/Admin/PharmaCategoryHeader';
import TableWrapper from 'src/Components/GlobalComponents/TableWrapper';
import editIcon from '../../../assets/images/edit-icon.png';
import deleteIcon from '../../../assets/images/delete-icon.png';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { canWrite } from "src/helpers/helperFunctions";
import { updatePharmacyCategories } from 'src/Store/realTimeState';
interface IResponce {
    documents: IChildCategory[];
    paginated: IPaginated;
    resultCount: {
        mainCategories: number;
        subCategories: number;
        childCategories: number;
    };
};

const PChildCategories: React.FC = () => {
    const { userDetails } = useSelector((store: RootState) => store.appReducer);
    const hasWriteAccess = canWrite(userDetails, "pharmaInventory", false);
    const [isUpsert, setIsUpsert] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<IChildCategory | null>(null);

    // main categories & sub categories
    const [getLoader, setGetLoader] = useState<boolean>(false);
    const [allMainCategory, setAllMainCategory] = useState<{ value: string; label: string }[]>([]);
    const [allSubCategory, setAllSubCategory] = useState<{ value: string; label: string }[]>([]);
    // main categories & sub categories

    const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
    const [isLoader, setIsLoader] = useState<boolean>(false);
    const [displayData, setDisplayData] = useState<IChildCategory[]>([]);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string>("");
    const [queryParams, setQueryParams] = useState<IqueryParams>({
        page: 1,
        limit: 10,
        search: "",
        bool: false,
    });
    const [paginated, setPaginated] = useState<IPaginated>({
        currentPage: 1,
        totalPages: 1,
    }); // state for handle pagination like a current page, page limit & total pages
    const { pharmacyCategories } = useSelector((store: RootState) => store.realTimeState);
    const dispatch = useDispatch();
    // Function to toggle between create and edit mode
    const handleClose = () => {
        setIsEdit(null);
        setIsUpsert(prev => !prev);
    };

    const handlePageChange = (e: number) => {
        setQueryParams(prev => ({
            ...prev,
            page: e
        }));
    };

    // Function to set the component to edit mode and populate it with category data
    const handleEdit = (obj: IChildCategory) => {
        if (!hasWriteAccess) {
            return
        }
        setIsUpsert(true);
        setIsEdit(obj);
    };

    // Function to show the delete confirmation modal and set the ID of the category to be deleted
    const handleDelete = (id: string) => {
        if (!hasWriteAccess) {
            return
        }
        setDeleteModal(true);
        setDeleteId(id);
    };

    // Function to close the delete confirmation modal
    const deleteModalClose = () => {
        setDeleteModal(false);
        setDeleteId("");
    };

    // Function change search text for api query params
    const handleChangeText = (txt: string) => {
        setQueryParams(prev => ({
            ...prev,
            page: 1,
            search: txt
        }));
    };

    // Function to update the list of categories after creating or editing a category
    const handleUpdateList = () => {
        setQueryParams(prev => ({
            ...prev,
            bool: !prev.bool,
        }));
        setIsUpsert(false);
    };

    const printSubCategoryName = (id: string): string => {
        return allSubCategory?.find(v => v?.value === id)?.label || "";
    };

    const printMainCategoryName = (id: string): string => {
        return allMainCategory?.find(v => v?.value === id)?.label || "";
    };

    // API_CALL: Function to delete a category by its ID
    const deleteRequest = async () => {
        setDeleteLoader(true);
        const { res, err } = await httpRequest<any>({ method: "delete", path: `/child-category/${deleteId}`, params: {} });
        if (res) {
            toast("Child category deleted successfully.", { type: 'success' });
            setQueryParams(prev => ({
                ...prev,
                bool: !prev.bool,
                page: displayData?.length > 1 ? prev?.page : prev?.page > 1 ? prev?.page - 1 : prev?.page
            }));
            setDeleteModal(false);
            setDeleteId("");
        } else {
            toast(err, { type: 'error' });
        }
        setDeleteLoader(false);
    };

    // API_CALL: Function to fetch all main categories of pharmacy based on query parameters
    const fetch = async () => {
        setIsLoader(true);
        const { bool, ...remainingParams } = queryParams;
        const { res, err } = await httpRequest<IResponce>({ path: `/child-category/getAll?${qs.stringify(remainingParams)}` });
        if (res) {
            const { documents = [], paginated = { currentPage: 1, totalPages: 1 }, resultCount = { mainCategories: 0, subCategories: 0, childCategories: 0 } } = res;
            setDisplayData(documents);
            setPaginated(paginated);
            dispatch(updatePharmacyCategories(resultCount));
        } else {
            toast(err, { type: 'error' });
        }
        setIsLoader(false);
    };

    const fetchAllMainCategory = async () => {
        setGetLoader(true);
        const { res, err } = await httpRequest<{ documents: IMainCategory[] }>({ path: "/main-category/getAllMainCategory" });
        if (res) {
            const arr = res?.documents?.map(v => ({ value: v?._id, label: v?.categoryName })) || [];
            setAllMainCategory(arr);
        } else {
            toast(err, { type: "error" });
        }
        setGetLoader(false);
    };

    const fetchAllSubCategory = async () => {
        setGetLoader(true);
        const { res, err } = await httpRequest<{ documents: ISubCategory[] }>({ path: "/sub-category/getAllSubCategory" });
        if (res) {
            const arr = res?.documents?.map(v => ({ value: v?._id, label: v?.subCategoryName })) || [];
            setAllSubCategory(arr);
        } else {
            toast(err, { type: "error" });
        }
        setGetLoader(false);
    };

    useEffect(() => {
        fetchAllMainCategory();
        fetchAllSubCategory();
    }, []);

    useEffect(() => {
        fetch();
    }, [queryParams]);

    return (
        <>
            <ConfirmationModal
                show={deleteModal}
                onClose={deleteModalClose}
                title="Are you sure you want to delete?"
                leftBtnText="No"
                rightBtnText="Yes"
                onConfirm={deleteRequest}
                loader={deleteLoader}
            />
            <BreadCrumb
                current="Pharma Categories"
                previous="dashboard"
                navigateUrl="/admin"
                btnText={"Add Child Category"}
                btnShow={true}
                onPress={() => {
                    if (!hasWriteAccess) {
                        return
                    }
                    handleClose()
                }}
                isUpsert={isUpsert}
                btnClassName={`${!hasWriteAccess ? 'cursor-not-allowed' : ""}`}
                BtnId="add-child-category-btn"
            />
            {isUpsert ?
                <UpsertChildCategory
                    getLoader={getLoader}
                    onSuccess={handleUpdateList}
                    isEdit={isEdit}
                    allMainCategory={allMainCategory}
                />
                :
                <div className="table-container">
                    <PharmaCategoryHeader
                        searchApply={handleChangeText}
                        counts={pharmacyCategories}
                    />
                    <TableWrapper
                        isLoading={isLoader}
                        thead={["S.No", "Child Category Name", "Sub Category Name", "Main Category Name", "Action"]}
                        page={paginated?.currentPage || 1}
                        total={paginated?.totalPages || 0}
                        columnWidth="12% 25% 25% 25% 13%"
                        onChange={handlePageChange}
                    >
                        <div className='tbody-content-height'>
                            {displayData?.map((item: IChildCategory, index: number) => (
                                <ul key={index} className='font-size-16px tbody-light-table font-Poppins-Regular grid' style={{ gridTemplateColumns: "12% 25% 25% 25% 13%" }}>
                                    <li>{(paginated?.currentPage - 1) * queryParams.limit + (index + 1)}</li>
                                    <li>{item?.childCategoryName || ""}</li>
                                    <li>{printSubCategoryName(item?.subCategoryId)}</li>
                                    <li>{printMainCategoryName(item?.mainCategoryId)}</li>
                                    <li className='action-last-colum'>
                                        <img id={`edit-child-category-btn-${index + 1}`} onClick={() => handleEdit(item)} src={editIcon} alt="" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                                        <img id={`delete-child-category-btn-${index + 1}`} onClick={() => handleDelete(item?._id)} src={deleteIcon} alt="" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                                    </li>
                                </ul>
                            ))}
                        </div>
                    </TableWrapper>
                </div>
            }
        </>
    );
};

export default PChildCategories;